import {
  parseLocalCalendarDay,
  toCalendarDay,
} from '../../../utils/dateTimeUtils';
import {
  inputTokensKey,
  outputTokensKey,
  type CostChartDayRow,
  type CostChartMonthRow,
  type CostChartPoint,
  type CostChartViewMode,
} from '../costChartTypes';

type ViewMode = CostChartViewMode;

type MethodTotals = { cost: number; inputTokens: number; outputTokens: number };

export function isPositiveLimit(
  value: number | null | undefined,
): value is number {
  return value != null && Number.isFinite(value) && value > 0;
}

export function collectMethods(rows: Array<{ methodName: string }>): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    if (row.methodName) set.add(row.methodName);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * @param fromDay `YYYY-MM-DD`
 * @param toDay `YYYY-MM-DD`
 */
export function aggregateByDay(
  rows: CostChartDayRow[],
  fromDay: string,
  toDay: string,
  methods: string[],
): CostChartPoint[] {
  const map = new Map<string, Map<string, MethodTotals>>();
  for (const row of rows) {
    let dayMap = map.get(row.day);
    if (!dayMap) {
      dayMap = new Map();
      map.set(row.day, dayMap);
    }
    const prev = dayMap.get(row.methodName) ?? {
      cost: 0,
      inputTokens: 0,
      outputTokens: 0,
    };
    dayMap.set(row.methodName, {
      cost: prev.cost + (row.estimatedCosts ?? 0),
      inputTokens: prev.inputTokens + (row.inputTokens ?? 0),
      outputTokens: prev.outputTokens + (row.outputToken ?? 0),
    });
  }

  const result: CostChartPoint[] = [];
  let running = 0;
  const cursor = parseLocalCalendarDay(fromDay);
  const end = parseLocalCalendarDay(toDay);

  while (cursor <= end) {
    const label = toCalendarDay(cursor);
    const dayMap = map.get(label);
    const point: CostChartPoint = {
      label,
      cost: 0,
      inputTokens: 0,
      outputTokens: 0,
      runningTotal: 0,
    };
    for (const method of methods) {
      const totals = dayMap?.get(method) ?? {
        cost: 0,
        inputTokens: 0,
        outputTokens: 0,
      };
      point[method] = totals.cost;
      point[inputTokensKey(method)] = totals.inputTokens;
      point[outputTokensKey(method)] = totals.outputTokens;
      point.cost += totals.cost;
      point.inputTokens += totals.inputTokens;
      point.outputTokens += totals.outputTokens;
    }
    running += point.cost;
    point.runningTotal = running;
    result.push(point);
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}

export function aggregateByMonth(
  rows: CostChartMonthRow[],
  methods: string[],
): CostChartPoint[] {
  const map = new Map<string, Map<string, MethodTotals>>();
  for (const row of rows) {
    let monthMap = map.get(row.month);
    if (!monthMap) {
      monthMap = new Map();
      map.set(row.month, monthMap);
    }
    const prev = monthMap.get(row.methodName) ?? {
      cost: 0,
      inputTokens: 0,
      outputTokens: 0,
    };
    monthMap.set(row.methodName, {
      cost: prev.cost + (row.estimatedCosts ?? 0),
      inputTokens: prev.inputTokens + (row.inputTokens ?? 0),
      outputTokens: prev.outputTokens + (row.outputToken ?? 0),
    });
  }

  let running = 0;
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, monthMap]) => {
      const point: CostChartPoint = {
        label: month,
        cost: 0,
        inputTokens: 0,
        outputTokens: 0,
        runningTotal: 0,
      };
      for (const method of methods) {
        const totals = monthMap.get(method) ?? {
          cost: 0,
          inputTokens: 0,
          outputTokens: 0,
        };
        point[method] = totals.cost;
        point[inputTokensKey(method)] = totals.inputTokens;
        point[outputTokensKey(method)] = totals.outputTokens;
        point.cost += totals.cost;
        point.inputTokens += totals.inputTokens;
        point.outputTokens += totals.outputTokens;
      }
      running += point.cost;
      point.runningTotal = running;
      return point;
    });
}

export function formatXAxisLabel(value: string, mode: ViewMode): string {
  if (mode === 'daily') {
    return String(parseInt(value.slice(8), 10));
  }
  const [year, month] = value.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    year: '2-digit',
  });
}
